import { useRouter } from 'next/router';
import styled, {
  ThemeContext,
  css,
  keyframes,
  DefaultTheme
} from 'styled-components';
import { resetFilters } from '../../../../helpers/search-filters';
import FilterIcon from '../../../../new-components/svg-icons/FilterIcon';
import ResetIcon from '../../../../new-components/svg-icons/ResetIcon';
import Button from '../../../../new-components/ui/Button';
import ButtonTextIcon from '../../../../new-components/ui/ButtonTextIcon';
import { logEvent } from '../../../../new-lib/analytics';
import { SearchFiltersActionTypes } from '../../../../reducers/search-filters/actions';
import { SearchFiltersTypes } from '../../../../reducers/search-filters/reducer';
import FilterContainer from '../../../search/components/filters/FilterContainer';
import MinMaxInputContainer from '../../../search/components/search-filters/min-max-view/MinMaxInputContainer';
import MultiSelectContainer from '../../../search/components/search-filters/multi-select-view/MultiSelectContainer';
import SingleInputContainer from '../../../search/components/search-filters/single-input-view/SingleInputContainer';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  SearchContext,
  SearchContextInterface
} from '../../../search-new/context/SearchContext';
import { useTranslation } from 'next-i18next';
import useOnClickOutside from '../../../../hooks/ClickOutside';
import CircleCloseIcon from '../../../../new-components/svg-icons/CircleCloseIcon';
import { unit_area_list } from '../../constants';
import FinancingFilter from '../../../search/components/search-filters/financing-filter/FinancingFilter';
import useMapSearch from '../../hooks/MapSearch';
import { useSelector } from 'react-redux';
import { DefaultMapEntity, ResultsType } from '../../types';
import PropertyFeaturesFilter from './PropertyFeatureFilter';

const FiltersModal = () => {
  const router = useRouter();
  const theme = useContext(ThemeContext) as DefaultTheme;
  const { t } = useTranslation(['search', 'common']);
  const compounds = useSelector((state: any) => state.map.compounds);
  const areas = useSelector((state: any) => state.map.areas);
  const resultsType = useSelector((state: any) => state.map.resultsType);
  const searchState = useSelector((state: any) => state.map.mapSearchStates);
  const filters = useSelector((state: any) => state.map.filters);

  const [count, setCount] = useState<{
    total: number;
    totalFiltered: number | null;
  }>({
    total:
      (resultsType === ResultsType.COMPOUNDS
        ? compounds?.length
        : areas?.length) ?? 0,
    totalFiltered:
      Object.keys(filters)?.length > 0
        ? resultsType === ResultsType.COMPOUNDS
          ? (compounds?.filter(
              (compound: DefaultMapEntity) => compound.isFiltered
            )?.length ?? 0)
          : (areas?.filter((area: DefaultMapEntity) => area.isFiltered)
              ?.length ?? 0)
        : null
  });

  const {
    setShowFilters,
    searchFiltersDispatch,
    filterOptions,
    searchFiltersState
  } = useContext(SearchContext) as SearchContextInterface;

  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const hasFiltersApplied = Object.keys(filters)?.length > 0;
    if (resultsType === ResultsType.COMPOUNDS) {
      setCount({
        total: compounds?.length ?? 0,
        totalFiltered: !hasFiltersApplied
          ? null
          : (compounds?.filter(
              (compound: DefaultMapEntity) => compound.isFiltered
            )?.length ?? 0)
      });
    } else {
      setCount({
        total: areas?.length ?? 0,
        totalFiltered: !hasFiltersApplied
          ? null
          : (areas?.filter((area: DefaultMapEntity) => area.isFiltered)
              ?.length ?? 0)
      });
    }
  }, [resultsType, areas, compounds, filters]);

  const getResultsCount = useCallback(() => {
    return count.totalFiltered ?? count.total;
  }, [count]);

  const getButtonText = useCallback(() => {
    if (searchState?.isFetching) {
      return t('loading');
    }
    if (count.totalFiltered !== null) {
      if (count.totalFiltered === 0) {
        return t('no_matching_results', { ns: 'common' });
      } else {
        return `${count.totalFiltered} ${t('out_of', { ns: 'common' })} ${count.total} ${t('results')}`;
      }
    } else if (count.total > 0) {
      return `${t('show')} ${count.total} ${t('results')}`;
    }
    return t('no_results', { ns: 'common' });
  }, [searchState, count]);

  const closeMenu = () => {
    setShowFilters(false);
    logEvent.search(
      'filter_show_results',
      'DesktopFiltersMenu',
      'filter',
      searchFiltersState
    );
  };

  useOnClickOutside(menuRef, menuRef, closeMenu);

  return (
    <Container ref={menuRef}>
      <div className="menu-header">
        <div className="menu-title">
          <FilterIcon />
          <span className="headline-2">{t('filter_by')}</span>
        </div>

        <div className="close-btn" onClick={closeMenu}>
          <CircleCloseIcon />
        </div>
      </div>

      <div className="filters-container hide-scrollbar">
        <FilterContainer name={t('property_features')}>
          <PropertyFeaturesFilter />
        </FilterContainer>

        <FilterContainer name={t('bedrooms')}>
          <MultiSelectContainer
            filterType={SearchFiltersTypes.BEDROOMS}
            maxDisplayedValue={5}
            shapeClass="circleShape"
          />
        </FilterContainer>

        <FilterContainer name={t('bathrooms')}>
          <MultiSelectContainer
            filterType={SearchFiltersTypes.BATHROOMS}
            maxDisplayedValue={5}
            shapeClass="circleShape"
          />
        </FilterContainer>

        <FilterContainer
          name={t('payments')}
          displayedValues={[
            {
              name: SearchFiltersTypes.DOWN_PAYMENT,
              value: searchFiltersState.searchFilters.down_payment,
              preValue: '',
              postValue: t('DP'),
              filterAction:
                SearchFiltersActionTypes.UPDATE_FILTERS_WITH_SINGLE_VALUE
            },
            {
              name: SearchFiltersTypes.MONTHLY_INSTALLMENTS,
              value: searchFiltersState.searchFilters.monthly_installments,
              preValue: '',
              postValue: t('monthly'),
              filterAction:
                SearchFiltersActionTypes.UPDATE_FILTERS_WITH_SINGLE_VALUE
            },
            {
              name: SearchFiltersTypes.INSTALLMENT_YEARS,
              value:
                searchFiltersState.searchFilters.installment_years.length > 0
                  ? searchFiltersState.searchFilters.installment_years
                  : undefined,
              preValue: '',
              postValue: t('years'),
              filterAction:
                SearchFiltersActionTypes.UPDATE_FILTERS_WITH_MULTISELECT
            }
          ]}
        >
          <div className="installment-wrapper">
            <SingleInputContainer
              filterType={SearchFiltersTypes.DOWN_PAYMENT}
              headline={t('down_payment')}
              curruncy={t('egp')}
              placeholder={`${t('max_amount')}`}
              min={parseInt(filterOptions.down_payment[0].value.toString())}
            />
            <SingleInputContainer
              filterType={SearchFiltersTypes.MONTHLY_INSTALLMENTS}
              headline={t('monthly_installments')}
              curruncy={t('egp')}
              min={parseInt(
                filterOptions.monthly_installments[0].value.toString()
              )}
              placeholder={`${t('max_amount')}`}
            />
          </div>
          <h3
            className="text-3"
            style={{ marginBottom: '8px', fontSize: '14px' }}
          >
            {`${t('years_of_installments')}`}
          </h3>
          <MultiSelectContainer
            filterType={SearchFiltersTypes.INSTALLMENT_YEARS}
            maxDisplayedValue={6}
            shapeClass="circleShape"
          />
        </FilterContainer>

        <FilterContainer
          name={t('square_meter')}
          subText={`(${t('m2')})`}
          displayedValues={[
            {
              name: SearchFiltersTypes.MIN_UNIT_AREA,
              value: searchFiltersState.searchFilters.min_unit_area,
              preValue: t('min'),
              postValue: '',
              filterAction:
                SearchFiltersActionTypes.UPDATE_FILTERS_WITH_SINGLE_VALUE
            },
            {
              name: SearchFiltersTypes.MAX_UNIT_AREA,
              value: searchFiltersState.searchFilters.max_unit_area,
              preValue: t('max'),
              postValue: '',
              filterAction:
                SearchFiltersActionTypes.UPDATE_FILTERS_WITH_SINGLE_VALUE
            }
          ]}
        >
          <MinMaxInputContainer
            maxFilterType={SearchFiltersTypes.MAX_UNIT_AREA}
            minFilterType={SearchFiltersTypes.MIN_UNIT_AREA}
            minList={unit_area_list}
            maxList={unit_area_list}
            min={parseInt(filterOptions.min_unit_area[0].value.toString())}
          />
        </FilterContainer>

        <FilterContainer name={t('finishing')}>
          <MultiSelectContainer
            filterType={SearchFiltersTypes.COMPLETION_TYPE}
            key={'Completion Type'}
            shapeClass="straightShape"
          />
        </FilterContainer>

        <FilterContainer name={t('sale_type')}>
          <MultiSelectContainer
            filterType={SearchFiltersTypes.SALE_TYPE}
            key={'Sale Type'}
            shapeClass="straightShape"
          />
        </FilterContainer>

        <FinancingFilter />
      </div>

      <ActionsContainer>
        <Button
          text={getButtonText()}
          backgroundColor={
            getResultsCount() > 0 ? theme.light.colors.primaryBlue : '#8e8e8e'
          }
          backgroundHover={'none'}
          fontSize={theme.font.sizes.s}
          padding="8px"
          color={theme.light.colors.white}
          hoverColor={theme.light.colors.white}
          border="none"
          onClick={closeMenu}
          disabled={getResultsCount() === 0}
        />

        <ButtonTextIcon
          text={t('reset_all')}
          backgroundColor={theme.light.colors.white}
          backgroundHover="#F4F7FA"
          fontSize={theme.font.sizes.s}
          padding="8px"
          color="#344054"
          hoverColor={theme.light.hoverColors.primaryBlue}
          border="1px solid #D0D5DD"
          icon={<ResetIcon />}
          onClick={() => {
            resetFilters(
              router,
              searchFiltersDispatch,
              !router.asPath.includes('/buy')
            );
            logEvent.search(
              'filter_reset',
              'DesktopFiltersMenu',
              'filter',
              searchFiltersState
            );
          }}
        />
      </ActionsContainer>
    </Container>
  );
};

// Animation for LTR layout
const slideInFromRight = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

// Animation for RTL layout
const slideInFromLeft = keyframes`
  from {
    transform: translateX(-100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const Container = styled.div`
  position: fixed;
  top: 45px;
  left: 0;
  right: 0;
  bottom: 0;
  margin: auto;
  z-index: 9999999999;
  display: flex;
  flex-direction: column;
  background-color: #fff;
  width: 100%;
  height: 100%;
  max-width: 600px;
  max-height: 80%;
  gap: 16px;
  padding: 16px;
  border-radius: 10px;
  animation: unset;

  .installment-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .filters-container {
    font-family: materialRegular;
    div.circleShape {
      font-size: 16px;
    }
    div.straightShape {
      font-size: 16px;
    }
  }

  .menu-header {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 0px;

    .menu-title {
      display: flex;
      align-items: center;
      gap: 16px;

      svg {
        width: 25px;
        height: auto;
        path {
          fill: #d9d9d9;
        }
      }

      span {
        font-family: materialBold;
        font-size: ${props => props.theme.font.sizes.xl};
        color: ${props => props.theme.light.colors.black};
      }
    }

    .close-btn {
      cursor: pointer;
      z-index: 99999;
    }
  }

  .filters-container {
    width: 100%;
    height: calc(100vh - 100px);
    overflow-y: auto;
    padding-bottom: 16px;
  }

  @media screen and (max-width: ${props =>
      props.theme.breakPoints.mobile.max}) {
    width: 100%;
    position: fixed;
    height: 100%;
    max-height: 100%;
    top: unset;
    opacity: 0;

    .installment-wrapper {
      width: 100%;
      display: flex;
      align-items: start;
      flex-direction: column !important;
      gap: 2px;

      div {
        width: 100% !important;
      }
    }

    ${({ dir }) =>
      dir === 'ar'
        ? css`
            right: 0;
            animation: ${slideInFromLeft} 0.1s ease forwards;
          `
        : css`
            right: 0;
            animation: ${slideInFromRight} 0.1s ease forwards;
          `}
    will-change: transform, opacity;
    will-change: transform, opacity;

    .filters-container {
      div.circleShape {
        font-size: 14px;
      }
      div.straightShape {
        font-size: 14px;
      }
    }
  }
`;

const ActionsContainer = styled.div`
  bottom: 0px;
  width: 100%;
  height: 100px;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  overflow: hidden;
  > div {
    width: 48%;
    button {
      width: 100%;
      cursor: pointer;
      height: 40px;
      border-radius: 8px;

      span {
        font-family: materialBold;
      }
    }
  }
`;

export default FiltersModal;
